import { PrimaryButton } from "components/designV2/buttons/primaryButton";
import { SecondaryButton } from "components/designV2/buttons/secondaryButton";
import { AvatarChip } from "components/designV2/avatarChip/avatarChip";
import { Checkbox } from "components/designV2/checkbox/checkbox";
import { CollapsibleSection } from "components/designV2/collapsibleSection/collapsibleSection";
import { Dropdown } from "components/designV2/dropdown/dropdown";
import { DropdownMenu } from "components/designV2/dropdownMenu/dropdownMenu";
import { InfoText } from "components/designV2/infoText/infoText";
import { InputField } from "components/designV2/inputfield/inputfield";
import { Grid, PageContainer } from "components/designV2/layout/grid";
import { ListRow } from "components/designV2/listRow/listRow";
import { Modal } from "components/designV2/modal/modal";
import { NavbarMenu } from "components/designV2/navbarmenu/navbarMenu";
import { PeriodNavigator } from "components/designV2/periodNavigator/periodNavigator";
import { SearchField } from "components/designV2/searchfield/searchfield";
import { SearchableDropdown } from "components/designV2/searchableDropdown/searchableDropdown";
import { SegmentController } from "components/designV2/segmentcontroller/segmentcontroller";
import { StatusBanner } from "components/designV2/statusBanner/statusBanner";
import { Tag } from "components/designV2/tag/tag";
import { TagLabelChip } from "components/designV2/tagLabelChip/tagLabelChip";
import { TextArea } from "components/designV2/textarea/textarea";
import { Text } from "components/designV2/text/text";
import { TimePicker } from "components/designV2/timePicker/timePicker";
import { DatePicker } from "components/designV2/datePicker/datePicker";
import { TooltipHover } from "components/designV2/tooltip/tooltip";
import { ColorV2 } from "config/colors";
import { TypographyV2 } from "config/font";
import { LocalDate, ZoneId } from "@js-joda/core";
import "@js-joda/timezone";
import { useMemo, useState } from "react";
import styled from "styled-components";
import { useCompanyStore } from "web-apps/company/stores/companyStore/companyStore";

const options = [
  { value: "full-time", label: "Full-time employee" },
  { value: "part-time", label: "Part-time employee" },
  { value: "freelance", label: "Freelance" },
];

const searchableOptions = options.map((option, index) => ({
  ...option,
  rightText: `ID: ${index + 1}`,
}));

const segmentOptions = [
  { value: "list", label: "List view", iconName: "v2/search" },
  { value: "calendar", label: "Calendar view", iconName: "v2/eye" },
  { value: "consolidated", label: "Consolidated view", iconName: "v2/plus" },
];

const segmentOptionsNoIcon = [
  { value: "list", label: "List view" },
  { value: "calendar", label: "Calendar view" },
  { value: "consolidated", label: "Consolidated view" },
];

const STOCKHOLM_TIME_ZONE = ZoneId.of("Europe/Stockholm");

const menuItems = [
  {
    value: "dashboard",
    label: "Dashboard",
    iconName: "v2/menu",
    href: "/design",
  },
  {
    value: "scheduling",
    label: "Scheduling",
    iconName: "v2/calendar",
    href: "/design",
  },
  {
    value: "organisation",
    label: "Organisation",
    iconName: "v2/person",
    href: "/design",
  },
  {
    value: "salaries",
    label: "Salaries",
    iconName: "v2/wallet",
    href: "/design",
  },
  {
    value: "time",
    label: "Time reports",
    iconName: "v2/clock",
    href: "/design",
  },
];

const dropdownMenuItems = [
  {
    value: "profile",
    label: "Profile",
    iconName: "v2/person",
    href: "/design",
  },
  {
    value: "settings",
    label: "Settings",
    iconName: "v2/settings",
    href: "/design",
  },
  {
    value: "companies",
    label: "Your companies",
    iconName: "v2/list",
    href: "/design",
  },
  { value: "add", label: "Add company", iconName: "v2/plus", href: "/design" },
  {
    value: "signout",
    label: "Sign out",
    iconName: "v2/logout",
    href: "/design",
    tone: "danger" as const,
  },
];

const colorEntries = Object.entries(ColorV2) as Array<
  [keyof typeof ColorV2, ColorV2]
>;

type TypographyVariant = keyof typeof TypographyV2;

const typographySamples: Array<{ name: TypographyVariant; label: string }> = [
  { name: "h1", label: "Heading 1" },
  { name: "h2", label: "Heading 2" },
  { name: "h3", label: "Heading 3" },
  { name: "h4", label: "Heading 4" },
  { name: "h5", label: "Heading 5" },
  { name: "b1", label: "Body Bold" },
  { name: "b2", label: "Body Medium" },
  { name: "s1", label: "Small" },
];

/** This is an example page for design components, not visible in prod */
export const DesignPage: React.FC = () => {
  const [value, setValue] = useState<string | undefined>();
  const [searchableValue, setSearchableValue] = useState<string | undefined>();
  const [email, setEmail] = useState("");
  const [searchValuePrimary, setSearchValuePrimary] = useState("");
  const [searchValueTags, setSearchValueTags] = useState("");
  const [searchTags, setSearchTags] = useState<string[]>([]);
  const [comment, setComment] = useState("");
  const [timeValue, setTimeValue] = useState("");
  const [timeCompactValue, setTimeCompactValue] = useState("");
  const [dateValue, setDateValue] = useState<LocalDate | null>(
    LocalDate.now(STOCKHOLM_TIME_ZONE),
  );
  const [dateCompactValue, setDateCompactValue] = useState<LocalDate | null>(
    LocalDate.now(STOCKHOLM_TIME_ZONE),
  );
  const [rangeDates, setRangeDates] = useState<LocalDate[]>([]);
  const [rangeOnlyDates, setRangeOnlyDates] = useState<LocalDate[]>([]);
  const [excludeHolidays, setExcludeHolidays] = useState(false);
  const [excludeWeekends, setExcludeWeekends] = useState(false);
  const [excludeNonWorkingDays, setExcludeNonWorkingDays] = useState(false);
  const [monthRestrictedDate, setMonthRestrictedDate] =
    useState<LocalDate | null>(LocalDate.now(STOCKHOLM_TIME_ZONE));
  const [segmentValue, setSegmentValue] = useState("list");
  const [segmentValueNoIcon, setSegmentValueNoIcon] = useState("list");
  const [activeMenu, setActiveMenu] = useState(menuItems[0].value);
  const [isCompanyMenuOpen, setIsCompanyMenuOpen] = useState(false);
  const [uncheckedValue, setUncheckedValue] = useState(false);
  const [checkedValue, setCheckedValue] = useState(true);
  const [touched, setTouched] = useState(false);
  const [companyState, , _companyDispatch] = useCompanyStore();
  const [periodMonthDate, setPeriodMonthDate] = useState<LocalDate>(
    LocalDate.now(),
  );
  const [periodYearDate, setPeriodYearDate] = useState<LocalDate>(
    LocalDate.now(),
  );
  const [periodWeekDate, setPeriodWeekDate] = useState<LocalDate>(
    LocalDate.now(),
  );
  const [periodDayDate, setPeriodDayDate] = useState<LocalDate>(
    LocalDate.now(),
  );
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isConfirmModalOpen, setIsConfirmModalOpen] = useState(false);
  const [isConfirmCloseModalOpen, setIsConfirmCloseModalOpen] =
    useState(false);
  const [modalSize, setModalSize] = useState<"small" | "medium" | "large">(
    "small",
  );

  const scrollToSection = (id: string) => {
    const element = document.getElementById(id);
    if (!element) return;
    element.scrollIntoView({ behavior: "smooth", block: "start" });
  };

  const hasError = useMemo(() => {
    if (!touched) return false;
    if (!email) return false;
    return !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }, [email, touched]);

  return (
    <Page>
      <SideNav>
        <SideNavTitle>Sections</SideNavTitle>
        <SideNavList>
          <SideNavLink onClick={() => scrollToSection("colors")}>
            Colors
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("inputs")}>
            Inputs
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("list-row")}>
            List Row
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("avatar-chip")}>
            Avatar Chip
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("search")}>
            Search
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("selection")}>
            Selection
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("buttons")}>
            Buttons
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("navigation")}>
            Navigation
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("modal")}>
            Modal
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("status-banner")}>
            Status Banner
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("period-navigation")}>
            Period Navigation
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("feedback")}>
            Feedback
          </SideNavLink>
          <SideNavLink onClick={() => scrollToSection("typography")}>
            Typography
          </SideNavLink>
        </SideNavList>
      </SideNav>
      <MainContent>
      <PageContainer>
        <Stack>
          <PageHeader>
            <PageTitle>Design System Components</PageTitle>
            <PageSubtitle>
              A structured overview of V2 components, grouped by function.
            </PageSubtitle>
          </PageHeader>

          <Section id="colors">
            <SectionHeader>
              <SectionTitle>Colors</SectionTitle>
              <SectionSubtitle>V2 palette tokens.</SectionSubtitle>
            </SectionHeader>
            <ColorGrid>
              {colorEntries.map(([name, value]) => (
                <ColorTile key={name}>
                  <ColorSwatch $color={value} />
                  <ColorLabel>{name}</ColorLabel>
                </ColorTile>
              ))}
            </ColorGrid>
          </Section>

          <Section id="inputs">
            <SectionHeader>
              <SectionTitle>Inputs</SectionTitle>
              <SectionSubtitle>Core data entry components.</SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <Dropdown
                    label="Label"
                    options={options}
                    value={value}
                    onChange={setValue}
                  />
                  <ComponentDescription>
                    Dropdown for selecting a single option from a predefined
                    list.
                    <ComponentName>Dropdown</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <SearchableDropdown
                    label="Searchable dropdown"
                    options={searchableOptions}
                    value={searchableValue}
                    onChange={setSearchableValue}
                    placeholder="Search option"
                  />
                  <ComponentDescription>
                    Dropdown with inline search and optional right-aligned text.
                    <ComponentName>SearchableDropdown</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <InputField
                    label="E-mail"
                    placeholder="name@domain.com"
                    value={email}
                    onChange={setEmail}
                    onClear={() => setEmail("")}
                    onBlur={() => setTouched(true)}
                    hasError={hasError}
                    errorText="The e-mail is wrong"
                    hintText="Inactive input field with hint text"
                  />
                  <ComponentDescription>
                    Text input with validation, clear action, and error
                    messaging.
                    <ComponentName>InputField</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <InputField
                    label="E-mail (disabled)"
                    value="patryk@simflexity.se"
                    disabled
                    hintText="Disabled input field"
                  />
                  <ComponentDescription>
                    Disabled input state with hint text for non-editable data.
                    <ComponentName>InputField</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TextArea
                    label="Comment"
                    placeholder="Comment"
                    value={comment}
                    onChange={setComment}
                    maxLength={300}
                    maxHeight={180}
                    hintText="Max 300 characters"
                  />
                  <ComponentDescription>
                    Multiline text area with max height and character limit.
                    <ComponentName>TextArea</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TimePicker
                    label="Start time"
                    value={timeValue}
                    onChange={setTimeValue}
                  />
                  <TimePicker
                    label="Start time (compact)"
                    value={timeCompactValue}
                    onChange={setTimeCompactValue}
                    size="compact"
                  />
                  <ComponentDescription>
                    Time picker with free input, hour/minute lists, and compact
                    size.
                    <ComponentName>TimePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
              <SectionColumn>
                <DemoGroup>
                  <Checkbox label="Label" checked="unchecked" disabled />
                  <ComponentDescription>
                    Disabled checkbox for read-only state examples.
                    <ComponentName>Checkbox</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <Checkbox
                    label="Label"
                    checked={uncheckedValue ? "checked" : "unchecked"}
                    onChange={setUncheckedValue}
                  />
                  <ComponentDescription>
                    Interactive checkbox that toggles a boolean value.
                    <ComponentName>Checkbox</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <Checkbox
                    label="Label"
                    checked={checkedValue ? "checked" : "unchecked"}
                    onChange={setCheckedValue}
                  />
                  <ComponentDescription>
                    Pre-checked checkbox to show selected state.
                    <ComponentName>Checkbox</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
            <CalendarGrid>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date"
                    value={dateValue}
                    onChange={setDateValue}
                  />
                  <DatePicker
                    label="Date (compact)"
                    value={dateCompactValue}
                    onChange={setDateCompactValue}
                    size="compact"
                  />
                  <ComponentDescription>
                    Date picker with range support and compact size.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (range picker)"
                    allowRangePicker
                    onRangeChange={setRangeDates}
                    value={rangeDates[0] ?? null}
                  />
                  <ComponentDescription>
                    Range selection with multi-date support.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (range only)"
                    rangeOnly
                    allowRangePicker
                    onRangeChange={setRangeOnlyDates}
                    value={rangeOnlyDates[0] ?? null}
                  />
                  <ComponentDescription>
                    Range-only mode without single-date toggle.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (exclude toggles)"
                    allowRangePicker
                    showExcludeToggles
                    excludeHolidays={excludeHolidays}
                    excludeWeekends={excludeWeekends}
                    onExcludeHolidaysChange={setExcludeHolidays}
                    onExcludeWeekendsChange={setExcludeWeekends}
                    onExcludeNonWorkingDaysChange={setExcludeNonWorkingDays}
                    onRangeChange={setRangeDates}
                  />
                  <ComponentDescription>
                    Exclude holidays/weekends toggles.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (toggles hidden)"
                    allowRangePicker
                    showExcludeToggles={false}
                    onRangeChange={setRangeDates}
                  />
                  <ComponentDescription>
                    Range picker without exclude toggles.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (exclude non-working days)"
                    allowRangePicker
                    showExcludeToggles
                    excludeNonWorkingDays={excludeNonWorkingDays}
                    onExcludeNonWorkingDaysChange={setExcludeNonWorkingDays}
                    onRangeChange={setRangeDates}
                  />
                  <ComponentDescription>
                    Legacy non-working day exclusion.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (monthly selectable)"
                    monthlySelectableDay={15}
                    value={dateValue}
                    onChange={setDateValue}
                  />
                  <ComponentDescription>
                    Only allows a specific day each month.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (min date)"
                    earliestSelectableDate={LocalDate.now(STOCKHOLM_TIME_ZONE)}
                    value={dateValue}
                    onChange={setDateValue}
                  />
                  <ComponentDescription>
                    Earliest selectable date constraint.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
              <CalendarItem>
                <DemoGroup>
                  <DatePicker
                    label="Date (month locked)"
                    restrictToMonth={
                      monthRestrictedDate ?? LocalDate.now(STOCKHOLM_TIME_ZONE)
                    }
                    disableNavigationOutsideMonth
                    value={monthRestrictedDate}
                    onChange={setMonthRestrictedDate}
                  />
                  <ComponentDescription>
                    Locks navigation to a specific month.
                    <ComponentName>DatePicker</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </CalendarItem>
            </CalendarGrid>
          </Section>

          <Section id="list-row">
            <SectionHeader>
              <SectionTitle>List Row</SectionTitle>
              <SectionSubtitle>
                List row containers with variants and selection.
              </SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <div
                    style={{ display: "flex", flexDirection: "column", gap: 12 }}
                  >
                    <ListRow variant="light">
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        <Text typography="b2" color={ColorV2.Blue1} align="left">
                          List row title
                        </Text>
                        <Text typography="s1" color={ColorV2.Blue4} align="left">
                          Secondary info
                        </Text>
                      </div>
                    </ListRow>
                    <ListRow variant="dark" selected>
                      <div
                        style={{
                          display: "flex",
                          flexDirection: "column",
                          gap: 4,
                        }}
                      >
                        <Text typography="b2" color={ColorV2.Blue1} align="left">
                          Selected row
                        </Text>
                        <Text typography="s1" color={ColorV2.Blue4} align="left">
                          Dark variant
                        </Text>
                      </div>
                    </ListRow>
                  </div>
                  <ComponentDescription>
                    List row container with light/dark variants and selection.
                    <ComponentName>ListRow</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="avatar-chip">
            <SectionHeader>
              <SectionTitle>Avatar Chip</SectionTitle>
              <SectionSubtitle>
                Compact identity chips with tags.
              </SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <AvatarChip
                    title="Peter Wisström"
                    subTitle="Kitchen"
                    tags={["Head Chef"]}
                  />
                  <ComponentDescription>
                    Avatar chip with title, subtitle, and tags.
                    <ComponentName>AvatarChip</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <AvatarChip
                    title="Staffing"
                    subTitle="Reception"
                    iconName="v2/roster"
                    tags={["Receptionist"]}
                  />
                  <ComponentDescription>
                    Avatar chip with icon and tags.
                    <ComponentName>AvatarChip</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TagLabelChip tagText="Reception" label="Receptionist" />
                  <ComponentDescription>
                    Tag + label chip without avatar or title.
                    <ComponentName>TagLabelChip</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="search">
            <SectionHeader>
              <SectionTitle>Search</SectionTitle>
              <SectionSubtitle>
                Search and query refinement patterns.
              </SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <SearchField
                    value={searchValuePrimary}
                    placeholder="Search team, unit, worker"
                    onChange={setSearchValuePrimary}
                    onClear={() => setSearchValuePrimary("")}
                  />
                  <ComponentDescription>
                    Basic search field for free-text queries.
                    <ComponentName>SearchField</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
              <SectionColumn>
                <DemoGroup>
                  <SearchField
                    value={searchValueTags}
                    placeholder="Search"
                    allowTags
                    tags={searchTags}
                    maxVisibleTags={2}
                    onTagsChange={setSearchTags}
                    onChange={setSearchValueTags}
                  />
                  <ComponentDescription>
                    Search field with tags to refine and structure queries.
                    <ComponentName>SearchField</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="selection">
            <SectionHeader>
              <SectionTitle>Selection</SectionTitle>
              <SectionSubtitle>Toggle and grouping controls.</SectionSubtitle>
            </SectionHeader>
            <SelectionGrid>
              <DemoGroup>
                <SegmentController
                  options={segmentOptions}
                  value={segmentValue}
                  onChange={setSegmentValue}
                />
                <ComponentDescription>
                  Segmented control for switching between views with icons.
                </ComponentDescription>
              </DemoGroup>
              <DemoGroup>
                <SegmentController
                  options={segmentOptionsNoIcon}
                  value={segmentValueNoIcon}
                  onChange={setSegmentValueNoIcon}
                  label="Group by"
                />
                <ComponentDescription>
                  Segmented control with label for grouping or filtering.
                </ComponentDescription>
              </DemoGroup>
            </SelectionGrid>
          </Section>

          <Section id="buttons">
            <SectionHeader>
              <SectionTitle>Buttons</SectionTitle>
              <SectionSubtitle>
                Primary and secondary button styles.
              </SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <ButtonRow>
                    <PrimaryButton label="Schedule" iconName="v2/plus" />
                    <PrimaryButton label="Schedule" />
                    <PrimaryButton iconName="v2/plus" aria-label="Schedule" />
                    <PrimaryButton
                      label="Schedule"
                      iconName="v2/plus"
                      disabled
                    />
                    <PrimaryButton
                      iconName="v2/plus"
                      aria-label="Schedule"
                      disabled
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Primary buttons in default, icon-only, and disabled states.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <ButtonRow>
                    <PrimaryButton
                      size="small"
                      label="Schedule"
                      iconName="v2/plus"
                    />
                    <PrimaryButton size="small" label="Schedule" />
                    <PrimaryButton
                      size="small"
                      iconName="v2/plus"
                      aria-label="Schedule"
                    />
                    <PrimaryButton
                      size="small"
                      label="Schedule"
                      iconName="v2/plus"
                      disabled
                    />
                    <PrimaryButton
                      size="small"
                      iconName="v2/plus"
                      aria-label="Schedule"
                      disabled
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Primary buttons in small size variations.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <ButtonRow>
                    <SecondaryButton label="Edit shift" />
                    <PrimaryButton
                      label="Remove shift"
                      iconName="v2/trash"
                      styleOverrides={{
                        backgroundColor: ColorV2.NegativeRed,
                        hoverBackgroundColor: ColorV2.DeclinedRed,
                        activeBackgroundColor: ColorV2.DeclinedRed,
                        textColor: ColorV2.White,
                        borderColor: "transparent",
                        iconColor: ColorV2.White,
                      }}
                    />
                  </ButtonRow>
                  <ButtonRow>
                    <SecondaryButton
                      label="Withdraw shift"
                      styleOverrides={{
                        textColor: ColorV2.NegativeRed,
                        borderColor: ColorV2.NegativeRed,
                        hoverBackgroundColor: ColorV2.WithdrawnPink,
                        activeBackgroundColor: ColorV2.WithdrawnPink,
                        iconColor: ColorV2.NegativeRed,
                      }}
                    />
                    <PrimaryButton
                      label="Decline"
                      iconName="v2/cross"
                      styleOverrides={{
                        backgroundColor: ColorV2.NegativeRed,
                        hoverBackgroundColor: ColorV2.DeclinedRed,
                        activeBackgroundColor: ColorV2.DeclinedRed,
                        textColor: ColorV2.White,
                        borderColor: "transparent",
                        iconColor: ColorV2.White,
                      }}
                    />
                    <PrimaryButton
                      label="Approve and pay"
                      iconName="v2/white-simflexity"
                      styleOverrides={{
                        backgroundColor: ColorV2.PositiveGreen,
                        hoverBackgroundColor: ColorV2.SimflexityGreen,
                        activeBackgroundColor: ColorV2.SimflexityGreen,
                        textColor: ColorV2.White,
                        borderColor: "transparent",
                        iconColor: ColorV2.White,
                      }}
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Action buttons with contextual V2 color variants.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
              <SectionColumn>
                <DemoGroup>
                  <ButtonRow>
                    <SecondaryButton label="Schedule" iconName="v2/plus" />
                    <SecondaryButton label="Schedule" />
                    <SecondaryButton iconName="v2/plus" aria-label="Schedule" />
                    <SecondaryButton
                      label="Schedule"
                      iconName="v2/plus"
                      disabled
                    />
                    <SecondaryButton
                      iconName="v2/plus"
                      aria-label="Schedule"
                      disabled
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Secondary buttons in default, icon-only, and disabled
                    states.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <ButtonRow>
                    <SecondaryButton
                      size="small"
                      label="Schedule"
                      iconName="v2/plus"
                    />
                    <SecondaryButton size="small" label="Schedule" />
                    <SecondaryButton
                      size="small"
                      iconName="v2/plus"
                      aria-label="Schedule"
                    />
                    <SecondaryButton
                      size="small"
                      label="Schedule"
                      iconName="v2/plus"
                      disabled
                    />
                    <SecondaryButton
                      size="small"
                      iconName="v2/plus"
                      aria-label="Schedule"
                      disabled
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Secondary buttons in small size variations.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <ButtonRow>
                    <PrimaryButton label="Saving" isLoading />
                    <SecondaryButton label="Loading" isLoading />
                    <PrimaryButton
                      iconName="v2/plus"
                      aria-label="Create"
                      isLoading
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Loading state disables interaction and shows a spinner.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="navigation">
            <SectionHeader>
              <SectionTitle>Navigation</SectionTitle>
              <SectionSubtitle>Menu and navigation patterns.</SectionSubtitle>
            </SectionHeader>
            <NavigationGrid>
              <SectionColumn>
                <DemoGroup>
                  <DropdownMenu
                    companyName={companyState.company?.companyName ?? ""}
                    items={dropdownMenuItems}
                    isOpen={isCompanyMenuOpen}
                    onToggle={setIsCompanyMenuOpen}
                    onItemClick={(item, event) => {
                      event.preventDefault();
                      console.log("click " + item.label);
                    }}
                    avatarSrc={companyState.companyImage ?? ""}
                  />
                  <ComponentDescription>
                    Dropdown menu for account and company actions.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <NavbarMenu
                    items={menuItems}
                    activeValue={activeMenu}
                    onItemClick={(item, event) => {
                      event.preventDefault();
                      setActiveMenu(item.value);
                    }}
                  />
                  <ComponentDescription>
                    Full-width navigation menu with active state handling.
                    Should be replaced with dropdown menu for smaller screens.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </NavigationGrid>
          </Section>

          <Section id="modal">
            <SectionHeader>
              <SectionTitle>Modal</SectionTitle>
              <SectionSubtitle>
                Modal shell with header and footer actions.
              </SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <ButtonRow>
                    <PrimaryButton
                      label="Open small"
                      onClick={() => {
                        setModalSize("small");
                        setIsModalOpen(true);
                      }}
                    />
                    <PrimaryButton
                      label="Open medium"
                      onClick={() => {
                        setModalSize("medium");
                        setIsModalOpen(true);
                      }}
                    />
                    <PrimaryButton
                      label="Open large"
                      onClick={() => {
                        setModalSize("large");
                        setIsModalOpen(true);
                      }}
                    />
                    <SecondaryButton
                      label="Open confirm"
                      onClick={() => setIsConfirmModalOpen(true)}
                    />
                    <SecondaryButton
                      label="Open with confirm"
                      onClick={() => {
                        setModalSize("small");
                        setIsConfirmCloseModalOpen(true);
                      }}
                    />
                  </ButtonRow>
                  <ComponentDescription>
                    Opens the modal in small, medium, or large size.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
            <Modal
              label="Decline"
              size={modalSize}
              showCloseButton
              isOpen={isModalOpen}
              onClose={() => setIsModalOpen(false)}
              footer={
                <>
                  <SecondaryButton
                    label="Cancel"
                    onClick={() => setIsModalOpen(false)}
                  />
                  <PrimaryButton
                    label="Decline"
                    iconName="v2/cross"
                    styleOverrides={{
                      backgroundColor: ColorV2.NegativeRed,
                      hoverBackgroundColor: ColorV2.DeclinedRed,
                      activeBackgroundColor: ColorV2.DeclinedRed,
                      textColor: ColorV2.White,
                      borderColor: "transparent",
                      iconColor: ColorV2.White,
                    }}
                    onClick={() => setIsModalOpen(false)}
                  />
                </>
              }
            >
              {modalSize === "small" ? (
                <TextArea
                  label="Comment"
                  placeholder="Add a short reason"
                  value={comment}
                  onChange={setComment}
                  maxLength={300}
                  maxHeight={180}
                  hintText="Max 300 characters"
                />
              ) : (
                <>
                  <CollapsibleSection
                    label="Shift details"
                    defaultOpen={false}
                  />
                  <CollapsibleSection label="Comment" defaultOpen>
                    <ModalText>
                      Lorem ipsum dolor sit amet, consectetur adipiscing elit,
                      sed do eiusmod tempor incididunt ut labore et dolore magna
                      aliqua. Ut enim ad minim veniam, quis nostrud exercitation
                      ullamco laboris nisi ut aliquip ex ea commodo consequat.
                    </ModalText>
                  </CollapsibleSection>
                </>
              )}
            </Modal>
            <Modal
              label="Are you sure?"
              size="small"
              variant="confirm"
              iconName="v2/trash"
              isOpen={isConfirmModalOpen}
              onClose={() => setIsConfirmModalOpen(false)}
              footer={
                <>
                  <SecondaryButton
                    label="Cancel"
                    onClick={() => setIsConfirmModalOpen(false)}
                  />
                  <PrimaryButton
                    label="Delete"
                    styleOverrides={{
                      backgroundColor: ColorV2.NegativeRed,
                      hoverBackgroundColor: ColorV2.DeclinedRed,
                      activeBackgroundColor: ColorV2.DeclinedRed,
                      textColor: ColorV2.White,
                      borderColor: "transparent",
                      iconColor: ColorV2.White,
                    }}
                    onClick={() => setIsConfirmModalOpen(false)}
                  />
                </>
              }
            >
              Deleting this is irreversible and may result in a worker not
              having a shift. Do you really want to delete this?
            </Modal>
            <Modal
              label="Decline"
              size="small"
              confirmOnClose
              isOpen={isConfirmCloseModalOpen}
              onClose={() => setIsConfirmCloseModalOpen(false)}
              footer={
                <>
                  <SecondaryButton
                    label="Cancel"
                    onClick={() => setIsConfirmCloseModalOpen(false)}
                  />
                  <PrimaryButton
                    label="Decline"
                    iconName="v2/cross"
                    styleOverrides={{
                      backgroundColor: ColorV2.NegativeRed,
                      hoverBackgroundColor: ColorV2.DeclinedRed,
                      activeBackgroundColor: ColorV2.DeclinedRed,
                      textColor: ColorV2.White,
                      borderColor: "transparent",
                      iconColor: ColorV2.White,
                    }}
                    onClick={() => setIsConfirmCloseModalOpen(false)}
                  />
                </>
              }
            >
              <TextArea
                label="Comment"
                placeholder="Add a short reason"
                value={comment}
                onChange={setComment}
                maxLength={300}
                maxHeight={180}
                hintText="Max 300 characters"
              />
            </Modal>
          </Section>

          <Section id="status-banner">
            <SectionHeader>
              <SectionTitle>Status Banner</SectionTitle>
              <SectionSubtitle>
                Inline status messaging with icon and labels.
              </SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <StatusBanner
                    iconName="v2/checkmark"
                    label="Absence approved - Sick"
                    subLabel="The absence has been approved"
                    textColor={ColorV2.Blue1}
                    backgroundColor={ColorV2.AbsenceLightBlue}
                  />
                  <ComponentDescription>
                    Status banner with default 936px width.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <StatusBanner
                    iconName="v2/cross"
                    label="Time report declined"
                    subLabel="The time report has been declined by you - the worker will have the opportunity to amend"
                    textColor={ColorV2.White}
                    backgroundColor={ColorV2.DeclinedRed}
                  />
                  <ComponentDescription>
                    Status banner for a declined state.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <StatusBanner
                    iconName="v2/plus"
                    label="Offered"
                    subLabel="The worker has been offered this shift but not accepted yet"
                    textColor={ColorV2.Blue1}
                    backgroundColor={ColorV2.OfferedYellow}
                  />
                  <ComponentDescription>
                    Status banner for an offered state.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="period-navigation">
            <SectionHeader>
              <SectionTitle>Period Navigation</SectionTitle>
              <SectionSubtitle>
                Year, month, and week navigation patterns.
              </SectionSubtitle>
            </SectionHeader>
            <ComponentDescription>
              Navigation is limited to 2022–2028 for this demo.
            </ComponentDescription>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <PeriodNavigator
                    currentDate={periodYearDate}
                    mode="year"
                    onChange={setPeriodYearDate}
                    prevAriaLabel="Previous year"
                    nextAriaLabel="Next year"
                    allowNext={periodYearDate.year() + 1 < 2028}
                    allowPrev={periodYearDate.year() - 1 >= 2022}
                  />
                  <ComponentDescription>
                    Period navigator for year-level navigation.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <PeriodNavigator
                    currentDate={periodMonthDate}
                    mode="month"
                    onChange={setPeriodMonthDate}
                    showSubLabel
                    prevAriaLabel="Previous month"
                    nextAriaLabel="Next month"
                    allowPrev={periodMonthDate.year() - 1 >= 2022}
                  />
                  <ComponentDescription>
                    Period navigator for month-level navigation.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <PeriodNavigator
                    currentDate={periodDayDate}
                    mode="day"
                    onChange={setPeriodDayDate}
                    showSubLabel
                    prevAriaLabel="Previous day"
                    nextAriaLabel="Next day"
                    allowPrev={periodDayDate.year() - 1 >= 2022}
                  />
                  <ComponentDescription>
                    Period navigator for day-level navigation (Swedish dates).
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <PeriodNavigator
                    currentDate={periodWeekDate}
                    mode="week"
                    onChange={setPeriodWeekDate}
                    showSubLabel
                    prevAriaLabel="Previous week"
                    nextAriaLabel="Next week"
                    allowPrev={periodWeekDate.year() - 1 >= 2022}
                  />
                  <ComponentDescription>
                    Period navigator for week-level navigation.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="feedback">
            <SectionHeader>
              <SectionTitle>Feedback</SectionTitle>
              <SectionSubtitle>Tags and tooltip helpers.</SectionSubtitle>
            </SectionHeader>
            <SectionGrid>
              <SectionColumn>
                <DemoGroup>
                  <Tag text="This is a tag" />
                  <ComponentDescription>
                    Tag for compact labeling or categorization.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <Tag text="This is a tag" onClear={() => {}} />
                  <ComponentDescription>
                    Removable tag with a clear action.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <InfoText
                    text="This is a text with extra info"
                    tooltipText="The break time is unpaid."
                    textStyle="b2"
                  />
                  <ComponentDescription>
                    Inline info text with tooltip.
                    <ComponentName>InfoText</ComponentName>
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
              <SectionColumn>
                <DemoGroup>
                  <TooltipHover
                    text="This is a little tooltip that explains what happens when you do something"
                    iconName="v2/search"
                    arrow="left"
                  >
                    <TooltipTrigger>Tooltip</TooltipTrigger>
                  </TooltipHover>
                  <ComponentDescription>
                    Tooltip that reveals extra guidance on hover.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TooltipHover
                    text="This tooltip appears above the trigger"
                    iconName="v2/eye"
                    arrow="down"
                  >
                    <TooltipTrigger>Tooltip (top)</TooltipTrigger>
                  </TooltipHover>
                  <ComponentDescription>
                    Tooltip positioned above the trigger text.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TooltipHover
                    text="This tooltip appears below the trigger"
                    iconName="v2/plus"
                    arrow="up"
                  >
                    <TooltipTrigger>Tooltip (bottom)</TooltipTrigger>
                  </TooltipHover>
                  <ComponentDescription>
                    Tooltip positioned below the trigger text.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TooltipHover
                    text="This tooltip appears to the right"
                    iconName="v2/search"
                    arrow="left"
                  >
                    <TooltipTrigger>Tooltip (right)</TooltipTrigger>
                  </TooltipHover>
                  <ComponentDescription>
                    Tooltip positioned to the right of the trigger text.
                  </ComponentDescription>
                </DemoGroup>
                <DemoGroup>
                  <TooltipHover
                    text="This tooltip appears to the left"
                    iconName="v2/search"
                    arrow="right"
                  >
                    <TooltipTrigger>Tooltip (left)</TooltipTrigger>
                  </TooltipHover>
                  <ComponentDescription>
                    Tooltip positioned to the left of the trigger text.
                  </ComponentDescription>
                </DemoGroup>
              </SectionColumn>
            </SectionGrid>
          </Section>

          <Section id="typography">
            <SectionHeader>
              <SectionTitle>Typography</SectionTitle>
              <SectionSubtitle>V2 font styles and scales.</SectionSubtitle>
            </SectionHeader>
            <TypographyList>
              {typographySamples.map((sample) => (
                <TypographyRow key={sample.name}>
                  <TypographyMeta>
                    <TypographyName>{sample.name}</TypographyName>
                    <TypographyLabel>{sample.label}</TypographyLabel>
                  </TypographyMeta>
                  <TypographySample $variant={sample.name}>
                    The quick brown fox jumps over the lazy dog
                  </TypographySample>
                </TypographyRow>
              ))}
            </TypographyList>
          </Section>
        </Stack>
      </PageContainer>
      </MainContent>
    </Page>
  );
};

const Stack = styled.div`
  display: flex;
  flex-direction: column;
  gap: 56px;
  align-items: flex-start;
  padding: 120px 0 64px;
  width: 100%;
`;

const Page = styled.div`
  background-color: ${ColorV2.Blue7};
`;

const SideNav = styled.aside`
  position: fixed;
  top: 120px;
  left: 24px;
  width: 200px;
  display: flex;
  flex-direction: column;
  gap: 12px;
  z-index: 5;
`;

const SideNavTitle = styled.div`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
`;

const SideNavList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SideNavLink = styled.a`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue2};
  text-decoration: none;

  &:hover {
    color: ${ColorV2.Blue1};
  }
`;

const MainContent = styled.div`
  margin-left: 240px;
`;

const DesktopOnly = styled.div`
  width: 100%;

  @media (max-width: 767px) {
    display: none;
  }
`;

const MobileOnly = styled.div`
  display: none;
  width: 100%;

  @media (max-width: 767px) {
    display: flex;
  }
`;

const ButtonRow = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: 16px;
  width: 100%;
`;

const TooltipTrigger = styled.span`
  ${TypographyV2.b2};
  text-decoration: underline;
  cursor: default;
`;

const ComponentDescription = styled.p`
  ${TypographyV2.b2};
  margin: 4px 0 0;
  color: ${ColorV2.Blue2};
  display: flex;
  flex-wrap: wrap;
  column-gap: 8px;
  row-gap: 4px;
`;

const ComponentName = styled.span`
  ${TypographyV2.b2};
  color: ${ColorV2.PositiveGreen};
  order: -1;
`;

const PageHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 12px;
  width: 100%;
`;

const PageTitle = styled.h1`
  ${TypographyV2.h3};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

const PageSubtitle = styled.p`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue2};
  margin: 0;
`;

const Section = styled.section`
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
`;

const SectionHeader = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
`;

const SectionTitle = styled.h2`
  ${TypographyV2.h5};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

const SectionSubtitle = styled.p`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue2};
  margin: 0;
`;

const SectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 32px;
  width: 100%;
  align-items: start;

  @media (min-width: 1024px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }
`;

const CalendarGrid = styled(Grid)`
  margin-top: 32px;
`;

const CalendarItem = styled.div`
  grid-column: span 4;

  @media (min-width: 1024px) {
    grid-column: span 6;
  }
`;

const ColorGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fill, minmax(140px, 1fr));
  column-gap: 24px;
  row-gap: 16px;
  width: 100%;
`;

const ColorTile = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 8px;
`;

const ColorSwatch = styled.div<{ $color: string }>`
  width: 56px;
  height: 56px;
  border-radius: 50%;
  background-color: ${(props) => props.$color};
  border: 1px solid ${ColorV2.Blue6};
`;

const ColorLabel = styled.span`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue2};
  text-align: center;
`;

const TypographyList = styled.div`
  display: flex;
  flex-direction: column;
  gap: 16px;
  width: 100%;
`;

const TypographyRow = styled.div`
  display: grid;
  grid-template-columns: 140px minmax(0, 1fr);
  gap: 16px;
  align-items: center;

  @media (max-width: 768px) {
    grid-template-columns: 1fr;
  }
`;

const TypographyMeta = styled.div`
  display: flex;
  flex-direction: column;
  gap: 4px;
`;

const TypographyName = styled.span`
  ${TypographyV2.b1};
  color: ${ColorV2.Blue1};
`;

const TypographyLabel = styled.span`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue3};
`;

const TypographySample = styled.p<{ $variant: TypographyVariant }>`
  ${({ $variant }) => TypographyV2[$variant]};
  margin: 0;
  color: ${ColorV2.Blue1};
`;

const ModalText = styled.p`
  ${TypographyV2.b2};
  color: ${ColorV2.Blue1};
  margin: 0;
`;

const NavigationGrid = styled(SectionGrid)`
  @media (min-width: 1024px) {
    grid-template-columns: 1fr;
  }
`;

const SelectionGrid = styled.div`
  display: grid;
  grid-template-columns: 1fr;
  gap: 24px;
  width: 100%;

  @media (min-width: 1400px) {
    grid-template-columns: repeat(2, minmax(0, 1fr));
    column-gap: 48px;
  }
`;

const SectionColumn = styled.div`
  display: flex;
  flex-direction: column;
  gap: 20px;
  align-items: flex-start;
  width: 100%;
  min-width: 0;
`;

const DemoGroup = styled.div`
  display: flex;
  flex-direction: column;
  gap: 8px;
  width: 100%;
`;
